import { useTranslation } from "react-i18next";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldRadio,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import svgIcons from "../../../assets/svg/svgIcons";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  StandardReportFieldResponse,
  TReportField,
} from "../../../api/types/Report";
import { APP_CONFIG } from "../../../constants/constants";
import { useGetReportAllFieldsQuery } from "../../../api/network/reportApiServices";

interface IPopupReportCalculation {
  wrapperClass?: string;
  reportId: number;
  onApplyReportColumnCalc?: (
    fieldId: string,
    aggregate_function?: string,
    column_type?: number,
    value?: number,
    operator?: string
  ) => void;
  fieldId: string;
  selected?: TReportField;
}

enum AGGREGATE_OPTIONS {
  AVERAGE = "average",
  SUM = "sum",
  MULTIPLY = "multiply",
  COUNT = "count",
  MIN = "min",
  MAX = "max",
}
export enum AGGREGATE_FUNCTION {
  SUM = "SUM",
  AVG = "AVG",
  MIN = "MIN",
  MAX = "MAX",
  COUNT = "COUNT",
}
export enum AGGREGATE_OPERATOR {
  MULTIPLY = "*",
}
export enum COLUMN_TYPE {
  STANDARD = 1,
  CUSTOM = 2,
  AGGREGATE = 3,
  AGGREGATE_CUSTOM = 4,
}
const AGG_OPTIONS_TO_FUNC = {
  [AGGREGATE_OPTIONS.AVERAGE]: AGGREGATE_FUNCTION.AVG,
  [AGGREGATE_OPTIONS.SUM]: AGGREGATE_FUNCTION.SUM,
  [AGGREGATE_OPTIONS.COUNT]: AGGREGATE_FUNCTION.COUNT,
  [AGGREGATE_OPTIONS.MIN]: AGGREGATE_FUNCTION.MIN,
  [AGGREGATE_OPTIONS.MAX]: AGGREGATE_FUNCTION.MAX,
  [AGGREGATE_OPTIONS.MULTIPLY]: AGGREGATE_OPERATOR.MULTIPLY,
};
const FUNC_TO_AGG_OPTIONS = {
  [AGGREGATE_FUNCTION.AVG]: AGGREGATE_OPTIONS.AVERAGE,
  [AGGREGATE_FUNCTION.SUM]: AGGREGATE_OPTIONS.SUM,
  [AGGREGATE_FUNCTION.COUNT]: AGGREGATE_OPTIONS.COUNT,
  [AGGREGATE_FUNCTION.MIN]: AGGREGATE_OPTIONS.MIN,
  [AGGREGATE_FUNCTION.MAX]: AGGREGATE_OPTIONS.MAX,
  [AGGREGATE_OPERATOR.MULTIPLY]: AGGREGATE_OPTIONS.MULTIPLY,
};

export enum AGGREGATION_TYPES {
  SUM = "sum",
  AVERAGE = "average",
  COUNT = "count",
}

export const PopupReportColumnAggregation = ({
  wrapperClass = "absolute top-full mt-1 flex flex-col gap-2 py-2 bg-white border border-border-gray rounded-lg shadow-lg w-64 z-popup",
  reportId,
  onApplyReportColumnCalc,
  fieldId,
  selected,
}: IPopupReportCalculation) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.columnCalculations",
  });
  //api
  const standaredReportFieldsQueryParams = {
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    search: "",
    report: reportId || 0,
    is_custom: true,
  };
  const { data: dataReportAllFields, isFetching: isFetchingReportAllFields } =
    useGetReportAllFieldsQuery(standaredReportFieldsQueryParams, {
      skip: standaredReportFieldsQueryParams.report === 0,
    });

  // State to manage Multiply popup visibility
  const [showMultiplyPopup, setShowMultiplyPopup] = useState(false);
  const [multiplyInputValue, setMultiplyInputValue] = useState<number>();
  const [multiplyAggregateType, setMultiplyAggregateType] = useState<
    AGGREGATION_TYPES | undefined
  >();
  //State to manage selected option
  const [selectedOption, setSelectedOption] =
    useState<AGGREGATE_OPTIONS | null>(null);
  useEffect(() => {
    if (selected) {
      const selectedVal =
        selected?.column_type === COLUMN_TYPE.AGGREGATE_CUSTOM &&
        selected.aggregate_function !== ""
          ? AGGREGATE_OPTIONS.MULTIPLY
          : FUNC_TO_AGG_OPTIONS[
              selected?.aggregate_function as AGGREGATE_FUNCTION
            ];
      setSelectedOption(selectedVal);
    }
  }, [selected]);
  const handleApply = () => {
    const option = selectedOption as AGGREGATE_OPTIONS;
    onApplyReportColumnCalc?.(
      field?.id.toString(),
      AGG_OPTIONS_TO_FUNC[
        option !== AGGREGATE_OPTIONS.MULTIPLY
          ? option
          : multiplyAggregateType || AGGREGATE_OPTIONS.SUM
      ],
      option === AGGREGATE_OPTIONS.MULTIPLY
        ? COLUMN_TYPE.AGGREGATE_CUSTOM
        : COLUMN_TYPE.AGGREGATE,
      multiplyInputValue,
      option === AGGREGATE_OPTIONS.MULTIPLY ? "*" : undefined
    );
  };

  const field = dataReportAllFields?.results.find(
    (field) => field.identifier === selected?.identifier
  ) as StandardReportFieldResponse;

  // Toggle the visibility of the popup
  const toggleMultiplyPopup = () => {
    setShowMultiplyPopup((prev) => !prev);
  };
  const handleOptionChange = (option: AGGREGATE_OPTIONS) => {
    setSelectedOption(option);
    setShowMultiplyPopup(option === AGGREGATE_OPTIONS.MULTIPLY);
  };
  return (
    <div className={`${wrapperClass}`}>
      <h4 className="text-left px-4 font-bold my-2 text-black/50">
        {t("heading")}
      </h4>

      <div className="flex flex-col">
        {Object.values(AGGREGATE_OPTIONS).map((option) => (
          <div
            key={option}
            className="relative flex items-center justify-between font-semibold"
            onClick={() => handleOptionChange(option)}
          >
            <AdminFormFieldRadio
              id={option}
              label={t(`${option}`)}
              type="radio"
              name={""}
              customWrapperClass="flex-grow flex justify-start px-4 py-2 hover:bg-accent-blue-paleO66 cursor-pointer"
              checked={selectedOption === option}
            />

            {/* Multiply Icon */}
            {option === AGGREGATE_OPTIONS.MULTIPLY && (
              <>
                <img
                  src={svgIcons.ExpandIcon}
                  className="size-6 absolute right-3 opacity-50 pointer-events-none touch-none"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents event conflicts
                    toggleMultiplyPopup();
                  }}
                />
                {/* Multiply Popup */}
                {showMultiplyPopup && (
                  <div className="absolute left-full ml-3 -top-2">
                    <PopupReportColumnMultiply
                      inputValue={multiplyInputValue}
                      setInputValue={setMultiplyInputValue}
                      aggregateType={multiplyAggregateType}
                      setAggregateType={setMultiplyAggregateType}
                      handleApply={handleApply}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 px-2">
        <AdminFormFieldSubmit
          variant="primary-alt"
          label={"Reset"}
          type="button"
          onClick={() => {
            setMultiplyAggregateType(undefined);
            setSelectedOption(null);
            setMultiplyInputValue(undefined);
            if (selected?.aggregate_function !== "") {
              onApplyReportColumnCalc?.(
                field?.id.toString(),
                undefined,
                undefined,
                undefined,
                undefined
              );
            }
          }}
        />
        <AdminFormFieldSubmit
          label={"Apply"}
          variant="success"
          type="button"
          disabled={
            !selectedOption || selectedOption === AGGREGATE_OPTIONS.MULTIPLY
          }
          onClick={handleApply}
        />
      </div>
    </div>
  );
};
interface PopupReportColumnMultiplyProps {
  inputValue?: number;
  setInputValue: Dispatch<SetStateAction<number | undefined>>;
  aggregateType: AGGREGATION_TYPES | undefined;
  setAggregateType: Dispatch<SetStateAction<AGGREGATION_TYPES | undefined>>;
  handleApply: () => void;
}

export const PopupReportColumnMultiply = ({
  inputValue,
  setInputValue,
  aggregateType,
  setAggregateType,
  handleApply,
}: PopupReportColumnMultiplyProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.columnCalculations",
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!isNaN(Number(newValue))) {
      setInputValue(Number(newValue));
    }
  };

  return (
    <div className="absolute top-full mt-1 flex flex-col gap-2 py-2 bg-white border border-border-gray rounded-lg shadow-lg w-64 z-popup">
      <span className="text-left px-4 font-bold my-2 text-black/50">
        {t("sub_heading")}
      </span>
      <div className="flex flex-1 items-center w-full px-4 gap-2">
        <AdminFormFieldInput
          label={" "}
          type={""}
          id={""}
          name={""}
          placeholder="Value"
          customWrapperClass="w-24"
          customInputClass="border rounded-md"
          value={inputValue}
          onChange={handleInputChange}
        />
        <span className="text-left px-2 font-medium">*</span>
        <AdminFormFieldDropdown
          label={""}
          id={"agg_types"}
          name={"agg_types"}
          customWrapperClass="w-[10em] text-left"
          options={Object.values(AGGREGATION_TYPES).map((option) => ({
            value: option,
            label: t(`${option}`),
          }))}
          value={aggregateType}
          onChange={(e: TSelectboxOption | null) => {
            if (e) {
              setAggregateType(e.value as AGGREGATION_TYPES);
            }
          }}
        />
      </div>

      <div className="flex justify-end gap-2 px-2 pt-2 pb-1">
        <AdminFormFieldSubmit
          variant="primary-alt"
          label={"Reset"}
          type="button"
          onClick={() => {
            setInputValue(0);
            setAggregateType(undefined);
          }}
        />
        <AdminFormFieldSubmit
          label={"Apply"}
          variant="success"
          type="button"
          onClick={handleApply}
          disabled={!inputValue || !aggregateType}
        />
      </div>
    </div>
  );
};
